import { Component, Prop, Ref } from 'vue-property-decorator'
import BaseSearcher from '@/components/common/searcher/components/base/base-searcher'
import {
    CustomIdentifierSearcherRequestParams,
    CustomIdentifierSearcherState,
    initialCustomIdentifierSearcherState
} from '@/components/common/searcher/components/custom-identifier/custom-identifier-searcher.model'
import { SearcherStatus } from '@/components/common/searcher/shared/models/common-searcher.model'
import { store } from '@/store/store'
import { Nullable } from '@/shared/types/app.types'
import { CustomValidationUtils } from '@/shared/util/custom-validation-utils'

@Component
export default class CustomIdentifierSearcher<I, T> extends BaseSearcher<CustomIdentifierSearcherRequestParams<I>, T> {
    @Prop({ type: String, default: 'custom-identifier-searcher' }) readonly qaId!: string
    @Prop({ type: Boolean, default: true }) readonly autofocus!: boolean

    @Prop({ type: Function, default: () => [] }) readonly getCustomIdentifiers!: () => I[]
    @Prop({ type: String }) readonly customIdentifierIndex!: Nullable<keyof I>
    @Prop({ type: String }) readonly customIdentifierLabel!: Nullable<keyof I>
    @Prop({ type: Object }) readonly initialCustomIdentifier!: Nullable<I>

    @Prop({ type: Boolean, default: false }) readonly hideCustomIdentifierSelector!: boolean

    @Ref() readonly identifierInput!: any

    state: CustomIdentifierSearcherState<I>
    constructor() {
        super()
        this.state = initialCustomIdentifierSearcherState()
    }

    get visibleCustomIdentifierSelector(): boolean {
        return !this.hideCustomIdentifierSelector &&
            !!this.customIdentifierIndex &&
            !!this.customIdentifierLabel &&
            !!this.state.customIdentifiers.length
    }

    ///

    focus(): void {
        if (this.identifierInput) {
            this.identifierInput.focus()
        }
    }

    private _loadInitialValues(): void {
        if (!!this.initialCustomIdentifier && !!this.customIdentifierIndex) {
            this.state.customIdentifier =
                this.state.customIdentifiers.find(
                    customIdentifier => customIdentifier[this.customIdentifierIndex!] === this.initialCustomIdentifier![this.customIdentifierIndex!])
        }
    }

    /// VUE HOOKS ///

    mounted(): void {
        if (!this.hideCustomIdentifierSelector) {
            this.state.customIdentifiers = this.getCustomIdentifiers() ?? []
        }
        this._loadInitialValues()
        this.state.loaded = true

        if (this.autofocus) {
            this.focus()
        }
    }

    ///

    async search(): Promise<void> {
        const { id, customIdentifier } = this.state
        if (id) {
            if (!CustomValidationUtils.checkRegex(store.state.regex, id)) {
                this.searchStatus(SearcherStatus.INVALID_IDENTIFIER) // emits an event
                return
            }
            const params: CustomIdentifierSearcherRequestParams<I> = { id, customIdentifier }
            const response = await this.internalSearch(this.searcher, params)
            this.searched(response ?? []) // emits an event
            if (response) {
                if (!response.length) {
                    this.searchStatus(SearcherStatus.NO_RESULTS) // emits an event
                } else {
                    this.searchStatus(SearcherStatus.SEARCH_SUCCESS) // emits an event
                }
            }
        }
    }

    clearSearch(): void {
        const { status, id } = initialCustomIdentifierSearcherState()
        this.state = {
            ...this.state,
            status,
            id
        }
        this._loadInitialValues()
        this.searchStatus(SearcherStatus.SEARCH_CLEARED) // emits an event

        if (this.autofocus) {
            this.focus()
        }
    }
}
